<template>
  <div class="max-w-md w-full mx-auto px-4 py-16">
    <div class="pwa-card p-6 md:p-8">
      <div class="text-center mb-8">
        <div class="inline-flex items-center justify-center w-16 h-16 rounded-full bg-indigo-500/10 text-indigo-400 mb-4 border border-indigo-500/20">
          <svg class="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
          </svg>
        </div>
        <h2 class="text-2xl font-bold text-slate-100">Participant Login</h2>
        <p class="text-sm text-slate-400 mt-2">
          {{ store.event?.name || 'Loading event context...' }}
        </p>
      </div>

      <!-- General Error Display -->
      <div v-if="store.error" class="p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-sm text-red-400 mb-5 flex items-start gap-2">
        <svg class="w-5 h-5 shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <div>
          <span class="font-semibold">Login Error:</span>
          <p class="mt-0.5">{{ store.error }}</p>
        </div>
      </div>

      <!-- Step 1: Input WhatsApp Number -->
      <form v-if="step === 'phone'" @submit.prevent="handleSendOtp" class="space-y-5">
        <div>
          <label class="pwa-label" for="phone">WhatsApp Number</label>
          <input
            id="phone"
            v-model="phone"
            type="text"
            class="pwa-input text-lg tracking-wide"
            placeholder="e.g. 62812345678"
            required
            :disabled="store.loading"
          />
          <p class="text-xs text-slate-500 mt-1.5">
            Enter the registered country code prefix phone number.
          </p>
        </div>

        <button type="submit" :disabled="store.loading" class="pwa-btn-primary">
          <span v-if="store.loading" class="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></span>
          {{ store.loading ? 'Requesting OTP...' : 'Send OTP Code' }}
        </button>

        <div class="text-center pt-2">
          <button type="button" @click="goToRegister" class="text-sm text-indigo-400 hover:text-indigo-300 font-medium">
            Not registered yet? Register here
          </button>
        </div>
      </form>

      <!-- Step 2: Input OTP Verification Code -->
      <form v-else @submit.prevent="handleVerifyOtp" class="space-y-5">
        <div class="p-3 bg-indigo-500/10 border border-indigo-500/20 rounded-lg text-xs text-slate-300 text-center">
          OTP code was sent to <strong>{{ phone }}</strong>
        </div>

        <div>
          <label class="pwa-label" for="otp">Enter 6-Digit OTP</label>
          <input
            id="otp"
            v-model="otp"
            type="text"
            maxlength="6"
            class="pwa-input text-center text-2xl font-bold tracking-widest"
            placeholder="000000"
            required
            :disabled="store.loading"
          />
          <div class="flex justify-between items-center mt-2">
            <span class="text-xs text-slate-500">Expires in 5 minutes</span>
            <button
              type="button"
              :disabled="resendCountdown > 0 || store.loading"
              @click="handleSendOtp"
              class="text-xs font-semibold text-indigo-400 hover:text-indigo-300 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {{ resendCountdown > 0 ? `Resend OTP in ${resendCountdown}s` : 'Resend OTP' }}
            </button>
          </div>
        </div>

        <div class="space-y-3 pt-2">
          <button type="submit" :disabled="store.loading || otp.length !== 6" class="pwa-btn-primary">
            <span v-if="store.loading" class="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></span>
            {{ store.loading ? 'Verifying...' : 'Verify & Log In' }}
          </button>

          <button type="button" @click="step = 'phone'" class="pwa-btn-secondary">
            Change phone number
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useParticipantStore } from '../stores/participant'

const router = useRouter()
const route = useRoute()
const store = useParticipantStore()

const step = ref<'phone' | 'otp'>('phone')
const phone = ref('')
const otp = ref('')
const resendCountdown = ref(0)
let timerInterval: any = null

onMounted(async () => {
  const slug = route.params.slug as string
  await store.loadEventContext(slug)
})

onUnmounted(() => {
  stopTimer()
})

function startTimer() {
  stopTimer()
  resendCountdown.value = 60
  timerInterval = setInterval(() => {
    if (resendCountdown.value > 0) {
      resendCountdown.value--
    } else {
      stopTimer()
    }
  }, 1000)
}

function stopTimer() {
  if (timerInterval) {
    clearInterval(timerInterval)
    timerInterval = null
  }
}

async function handleSendOtp() {
  store.clearError()
  const success = await store.sendOtp(phone.value)
  if (success) {
    step.value = 'otp'
    startTimer()
  }
}

async function handleVerifyOtp() {
  store.clearError()
  const success = await store.verifyOtp(phone.value, otp.value)
  if (success) {
    router.push({
      name: 'dashboard',
      params: { slug: store.event?.slug || route.params.slug }
    })
  }
}

function goToRegister() {
  router.push({
    name: 'register',
    params: { slug: store.event?.slug || route.params.slug }
  })
}
</script>
